import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { OrderCard } from '@/components/orders/OrderCard';
import { PullIndicator } from '@/components/orders/PullIndicator';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { cn } from '@/lib/utils';
import type { OrderDTO } from '@/types/api';

export type ColumnTone = 'new' | 'preparing' | 'completed' | 'other';

interface OrderColumnProps {
  tone: ColumnTone;
  title: string;
  orders: OrderDTO[];
  newIds: Set<number>;
  emptyKey: string;
  onOpen: (id: number) => void;
  /** Show "View all in History" link at the bottom of the column. */
  showHistoryLink?: boolean;
  /** Hard cap on visible cards (the rest are still loaded; just not rendered here). */
  cap?: number;
  /** Async refresh handler — wires up pull-to-refresh on touch devices. */
  onRefresh?: () => Promise<unknown> | void;
}

const toneAccent: Record<ColumnTone, string> = {
  new: 'before:bg-primary',
  preparing: 'before:bg-warning',
  completed: 'before:bg-success',
  other: 'before:bg-muted-foreground',
};

export function OrderColumn({
  tone,
  title,
  orders,
  newIds,
  emptyKey,
  onOpen,
  showHistoryLink = false,
  cap,
  onRefresh,
}: OrderColumnProps) {
  const { t } = useTranslation();
  const visible = cap ? orders.slice(0, cap) : orders;
  const overflow = cap ? Math.max(0, orders.length - cap) : 0;

  const { ref, pullDistance, isRefreshing, threshold } = usePullToRefresh<HTMLDivElement>({
    onRefresh: onRefresh ?? (() => {}),
  });

  return (
    <section
      className={cn(
        'relative flex h-full shrink-0 snap-start snap-always flex-col overflow-hidden bg-muted/30',
        'w-[85vw] max-w-[440px] md:w-auto md:max-w-none md:shrink',
        'before:absolute before:left-0 before:top-0 before:z-20 before:h-1 before:w-full',
        toneAccent[tone],
      )}
    >
      <div
        ref={ref}
        className="scrollbar-thin flex min-h-0 flex-1 flex-col overflow-y-auto pt-3"
      >
        {onRefresh && (
          <PullIndicator distance={pullDistance} isRefreshing={isRefreshing} threshold={threshold} />
        )}

        <header className="sticky top-0 z-10 mb-3 flex items-center justify-between rounded-md bg-muted/80 px-3 py-2 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <span className="label-tracked text-[10px]">{title}</span>
            <span className="num-tabular rounded-full bg-card px-2 py-0.5 text-xs font-bold text-foreground shadow-[0_1px_0_0_hsl(var(--border))]">
              {orders.length}
            </span>
          </div>
        </header>

        {visible.length === 0 ? (
          <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-border/70 bg-card/40 px-3 text-center text-xs text-muted-foreground">
            {t(emptyKey)}
          </div>
        ) : (
          <div className="flex flex-col gap-3 pb-3">
            {visible.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                isNew={newIds.has(order.id)}
                onOpen={onOpen}
              />
            ))}
          </div>
        )}
      </div>

      {showHistoryLink && (overflow > 0 || visible.length > 0) && (
        <div className="shrink-0 border-t border-border/40 bg-muted/50 p-3">
          <Link
            to="/history"
            className="flex items-center justify-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
          >
            {overflow > 0
              ? t('orders.columns.viewMoreInHistory', { count: overflow })
              : t('orders.columns.viewInHistory')}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </section>
  );
}
